import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuthenticatedFetch } from './useAuthenticatedFetch';
import { useCurrentUser } from './useApi';
import { marketAccessCache } from '../services/market-access-cache';
import { entitlementsFetchManager } from '../services/entitlements-fetch-manager';
import { API_URL } from '../utils/config';
import { 
  UserEntitlementsSummary,
  EntitlementFilters,
  EntitlementDetails,
} from '../types/subscription';

interface UseUserEntitlementsOptions {
  filters?: EntitlementFilters;
  enabled?: boolean;
  onSuccess?: (data: UserEntitlementsSummary) => void;
  onError?: (error: Error) => void;
}

export function useUserEntitlements(options: UseUserEntitlementsOptions = {}) {
  const { filters, enabled = true, onSuccess, onError } = options;
  const [data, setData] = useState<UserEntitlementsSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const { authenticatedFetch } = useAuthenticatedFetch();
  const { data: user } = useCurrentUser();
  const authenticatedFetchRef = useRef(authenticatedFetch);

  // Update the ref when authenticatedFetch changes
  useEffect(() => {
    authenticatedFetchRef.current = authenticatedFetch;
  }, [authenticatedFetch]);

  const fetchEntitlements = useCallback(async () => {
    if (!enabled || !user) return;

    setLoading(true);
    setError(null);

    try {
      // Create a unique key for this request based on filters and user
      const paramsKey = JSON.stringify({
        userId: user.id,
        filters: filters || {},
      });

      // Use the fetch manager to prevent duplicate requests
      const responseData = await entitlementsFetchManager.fetchEntitlements(
        async () => {
          const params = new URLSearchParams();
          
          if (filters?.status) params.append('status', filters.status);
          if (filters?.accessLevel) params.append('accessLevel', filters.accessLevel);
          if (filters?.includeExpired !== undefined) {
            params.append('includeExpired', filters.includeExpired.toString());
          }

          const queryString = params.toString();
          const url = `${API_URL}/api/marketcompass/subscriptions/my-entitlements${
            queryString ? `?${queryString}` : ''
          }`;

          const response = await authenticatedFetchRef.current(url, {
            method: 'GET',
          });

          if (!response) {
            throw new Error('Failed to get authenticated response');
          }

          if (!response.ok) {
            throw new Error(`Failed to get entitlements: ${response.statusText}`);
          }

          return response.json();
        },
        paramsKey
      );

      setData(responseData);
      onSuccess?.(responseData);

      // Pre-populate cache with user's accessible markets
      if (responseData.entitlements.length > 0) {
        responseData.entitlements.forEach((entitlement: EntitlementDetails) => {
          if ((entitlement.status === 'active' || entitlement.status === 'expiring') && entitlement.marketId) {
            marketAccessCache.set(
              user.id,
              entitlement.marketId,
              true,
              entitlement.accessLevel,
              entitlement.validUntil?.toString()
            );
          }
        });
      }
    } catch (err: any) {
      setError(err);
      onError?.(err);
    } finally {
      setLoading(false);
    }
  }, [filters, enabled, user, onSuccess, onError]);

  useEffect(() => {
    fetchEntitlements();
  }, [fetchEntitlements]);

  const refetch = useCallback(() => {
    // Clear the cache when explicitly refetching
    entitlementsFetchManager.clearCache();
    return fetchEntitlements();
  }, [fetchEntitlements]);

  const hasEntitlement = useCallback((
    type: 'market' | 'country' | 'continent',
    id: number
  ): boolean => {
    if (!data) return false;
    
    return data.entitlements.some(e => 
      (e.status === 'active' || e.status === 'expiring') &&
      e.accessLevel === type &&
      ((type === 'market' && e.marketId === id) ||
       (type === 'country' && e.countryId === id) ||
       (type === 'continent' && e.continentId === id))
    );
  }, [data]);

  const getEntitlementsByLevel = useCallback((
    level: 'market' | 'country' | 'continent'
  ): EntitlementDetails[] => {
    if (!data) return [];
    
    return data.entitlements.filter(e => 
      (e.status === 'active' || e.status === 'expiring') && e.accessLevel === level
    );
  }, [data]);

  const getExpiringEntitlements = useCallback((): EntitlementDetails[] => {
    if (!data) return [];
    
    return data.entitlements.filter(e => e.status === 'expiring');
  }, [data]);

  return {
    data,
    entitlements: data?.entitlements || [],
    summary: data?.summary,
    accessibleCountries: data?.accessibleCountries || [],
    accessibleContinents: data?.accessibleContinents || [],
    loading,
    error,
    refetch,
    hasEntitlement,
    getEntitlementsByLevel,
    getExpiringEntitlements,
    isEmpty: data?.entitlements.length === 0,
    hasActiveSubscriptions: (data?.summary.active || 0) > 0,
  };
}